import { useMainDict } from "@/libs/hooks/useTranslation";
import Image from "next/image";
import { DeliveryLinksComponent } from "./DeliveryLinks";
import { AddressAndPhone } from "./AddressAndPhone";
import { appConfig } from "@/libs/appConfig";
import { TermsAndPoliciesLinks } from "../TermsAndPoliciesLinks";
import { useSettings } from "@/libs/hooks/useSettings";
import { useCopies } from "@/libs/hooks/useCopy";

export const MainBannerComponent = () => {
  const { t } = useMainDict();
  const { settings } = useSettings();
  const { copy } = useCopies();

  return (
    <section
      className="relative min-h-screen w-full overflow-hidden"
      aria-labelledby="main-banner-heading"
    >
      {/* Vintage paper background with grain texture */}
      <div className="absolute inset-0 bg-gradient-to-b from-vintage-cream via-vintage-parchment to-vintage-cream">
        {/* Decorative aged paper texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
        {/* Vignette effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(44,24,16,0.12)_100%)]" />
      </div>

      {/* Decorative corner ornaments */}
      <div className="absolute top-8 left-8 w-24 h-24 border-l-2 border-t-2 border-vintage-ochre/40 hidden md:block" />
      <div className="absolute top-8 right-8 w-24 h-24 border-r-2 border-t-2 border-vintage-ochre/40 hidden md:block" />
      <div className="absolute bottom-8 left-8 w-24 h-24 border-l-2 border-b-2 border-vintage-ochre/40 hidden md:block" />
      <div className="absolute bottom-8 right-8 w-24 h-24 border-r-2 border-b-2 border-vintage-ochre/40 hidden md:block" />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8">
        <div className="max-w-7xl w-full flex flex-col items-center">

          {/* Logo with vintage frame effect */}
          <div className="relative mb-6 vintage-fade-in">
            <div className="absolute -inset-4 border border-vintage-ochre/30 rounded-full" />
            <div className="absolute -inset-6 border border-vintage-ochre/20 rounded-full" />
            <Image
              src={"/newest.png"}
              width={140}
              height={140}
              alt="Pizzicletta logo"
              className="drop-shadow-lg"
            />
          </div>

          {/* Main headline with vintage typography */}
          <div className="text-center mb-8">
            <div className="vintage-fade-in-delay-1">
              {/* Decorative line above */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-ochre to-transparent" />
                <span className="text-vintage-ochre text-2xl">✦</span>
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-ochre to-transparent" />
              </div>

              <h1
                id="main-banner-heading"
                className="font-display font-black text-vintage-burgundy tracking-wide"
              >
                <span className="block text-5xl md:text-7xl lg:text-8xl leading-tight">
                  {t("mainBanner.its")}
                </span>
                <span className="block text-5xl md:text-7xl lg:text-8xl leading-tight mt-[-0.1em]">
                  {t("mainBanner.pizzaTime")}
                </span>
              </h1>

              {/* Decorative line below */}
              <div className="flex items-center justify-center gap-4 mt-4">
                <div className="w-24 h-px bg-gradient-to-r from-transparent via-vintage-ochre to-transparent" />
                <span className="text-vintage-ochre text-lg">◆</span>
                <div className="w-24 h-px bg-gradient-to-r from-transparent via-vintage-ochre to-transparent" />
              </div>
            </div>
          </div>

          {/* Two-column layout for larger screens */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 w-full">

            {/* Left side: Contact info and delivery */}
            <div className="flex flex-col items-center vintage-fade-in-delay-2 order-2 lg:order-1">
              <AddressAndPhone
                phoneNumber={settings?.phoneNumber}
                address={settings?.address}
                showOpeningHours={settings?.showOpeningHoursButton}
              />
              <DeliveryLinksComponent />
              <TermsAndPoliciesLinks showPrivacy={false} showPromotion={false} />
            </div>

            {/* Right side: Pizza image with vintage frame */}
            <div className="relative order-1 lg:order-2 vintage-fade-in-delay-1">
              {/* Decorative circular frame */}
              <div className="absolute -inset-8 rounded-full border-2 border-vintage-ochre/20 hidden lg:block" />
              <div className="absolute -inset-12 rounded-full border border-vintage-ochre/10 hidden lg:block" />

              {/* Vintage stamp badge */}
              <div className="absolute -top-4 -right-4 z-20 transform rotate-12 hidden md:block">
                <div className="bg-vintage-terracotta text-vintage-cream px-4 py-2 rounded font-condensed text-sm tracking-wider shadow-vintage">
                  <span className="block text-center">AUTENTICA</span>
                  <span className="block text-center text-xs">NAPOLETANA</span>
                </div>
              </div>

              <Image
                unoptimized={true}
                alt="Delicious margherita pizza"
                src={`${appConfig.assetsUrl}/uploads/margherita_adf472eb14.png`}
                width={550}
                height={550}
                className="w-auto max-w-[320px] md:max-w-[450px] lg:max-w-[550px] drop-shadow-2xl animate-float-slow"
                priority
              />
            </div>
          </div>

          {/* Tagline with script font */}
          <div className="mt-8 lg:mt-4 vintage-fade-in-delay-3">
            <h2 className="font-script text-3xl md:text-5xl lg:text-6xl text-vintage-terracotta text-center transform -rotate-2 drop-shadow-sm">
              {copy?.bannerCopy}
            </h2>
          </div>
        </div>
      </div>

      {/* Bottom decorative wave/border */}
      <div className="absolute bottom-0 left-0 right-0 h-8 overflow-hidden">
        <svg
          viewBox="0 0 1200 30"
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          <path
            d="M0,30 C300,10 600,25 900,10 C1050,5 1150,15 1200,10 L1200,30 Z"
            fill="#722F37"
            opacity="0.9"
          />
        </svg>
      </div>
    </section>
  );
};

export default MainBannerComponent;
